import { useQuery } from "@tanstack/react-query";
import httpClient from "../httpClient";
import { HttpError } from "../httpError";
import { portalContextKey } from "../../constants";
import { PortalContextResponse } from "../../models/theme";

// The two outcomes the endpoint distinguishes and the caller must not fold
// together: an unknown slug is a page that does not exist, an expired pay link
// is a page that did. Telling a payer their link has expired is actionable;
// telling them the page is unknown is not.
export const PORTAL_CONTEXT_NOT_FOUND = 404;
export const PORTAL_CONTEXT_EXPIRED = 410;

type PortalContextEntry = {
  slug?: string;
  paylink?: string;
  authorisation?: string;
  enabled?: boolean;
};

const useQueryPortalContext = ({
  slug,
  paylink,
  authorisation = "",
  enabled = true,
}: PortalContextEntry) => {
  // Exactly one of the two identifies an entry point. Sending both is a
  // caller mistake the server answers with a 400, and sending neither would
  // ask it to resolve nothing.
  const hasExactlyOneEntry = !!slug !== !!paylink;

  const query = useQuery<PortalContextResponse, HttpError>(
    portalContextKey({ slug, paylink }),
    () =>
      httpClient("portal/context", { authorisation }).get<
        PortalContextResponse
      >({ slug, paylink }),
    {
      // Resolved once per session: the answer only changes when the entry
      // point does, and the entry point is in the key.
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      // A 404 or a 410 is the server's answer, not a fault to retry. Retrying
      // would also make the not-found screen take three round trips to appear.
      retry: false,
      enabled: enabled && !!authorisation && hasExactlyOneEntry,
    }
  );

  const status = query.error?.status;

  return {
    ...query,
    // A disabled query is indistinguishable from a slow one -- both render a
    // spinner -- so the reason is readable rather than inferred from
    // `isLoading` being stuck.
    isWaitingForEntry: !hasExactlyOneEntry || !authorisation,
    isNotFound: status === PORTAL_CONTEXT_NOT_FOUND,
    isExpired: status === PORTAL_CONTEXT_EXPIRED,
  };
};

export default useQueryPortalContext;
